// src/components/discovery/RunDialog.jsx
// Trigger a discovery run — fires in the background, closes immediately.

import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, FormControl, InputLabel, Select, MenuItem,
  Alert, Typography,
} from '@mui/material';
import { triggerDiscoveryRun } from '../../api/discovery';
import { FS, SP } from '../../theme/tokens';
import { SOURCE_TIER_LABELS } from '../../utils/labels';

const TIER_OPTIONS = [
  { value: 'idealo',          label: SOURCE_TIER_LABELS.idealo },
  { value: 'geizhals',        label: SOURCE_TIER_LABELS.geizhals },
  { value: 'bing_shopping',   label: SOURCE_TIER_LABELS.bing_shopping },
  // Der Hinweis bleibt hier: er gehoert zu DIESER Auswahl, nicht in die Map.
  { value: 'serpapi',         label: `${SOURCE_TIER_LABELS.serpapi} (paid, gap-fill)` },
];

export default function RunDialog({ open, onClose, onRunStarted }) {
  const [sourceTier, setSourceTier] = useState('idealo');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  function handleClose() {
    setError(null);
    setSourceTier('idealo');
    onClose();
  }

  async function handleRun() {
    setLoading(true);
    setError(null);
    try {
      await triggerDiscoveryRun({ sourceTier, triggeredBy: 'ui' });
      onRunStarted?.({ sourceTier });
      handleClose();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not start run');
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: FS.xl }}>
        Trigger Discovery Run
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography sx={{ fontSize: FS.sm, color: 'text.secondary', mb: SP.card }}>
          Runs in the background — results appear in the inbox when complete.
        </Typography>

        <FormControl fullWidth size="small">
          <InputLabel id="run-tier-label">Source tier</InputLabel>
          <Select
            labelId="run-tier-label"
            value={sourceTier}
            label="Source tier"
            onChange={e => setSourceTier(e.target.value)}
            disabled={loading}
          >
            {TIER_OPTIONS.map(t => (
              <MenuItem key={t.value} value={t.value}>
                <Typography sx={{ fontSize: FS.base }}>{t.label}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {error && (
          <Alert severity="error" sx={{ mt: 2, fontSize: FS.sm }}>{error}</Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button variant="outlined" size="small" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" size="small" onClick={handleRun} disabled={loading}>
          {loading ? 'Starting…' : 'Start scan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
